/**
 * The three workspace data modes, mirroring `app/datamodes.py`.
 *
 * The list exists in the frontend for one reason: to name every mode the
 * product has, including the ones this server does not offer — a mode that is
 * silently omitted teaches the reader nothing. The server decides what is
 * selectable (`WorkspaceRef.data_mode_options`); a mode outside that list is
 * rendered without any control at all. There is no code path from this file to
 * a request that asks for a locked mode; the lock lives on the server (403 +
 * reason), this is only how the reader learns it exists and why.
 *
 * `live` is the mode that needs describing rather than merely locking. What it
 * means depends on this server's configuration and the server says which:
 * `WorkspaceRef.us_transfer` is true only when the extraction step for this
 * workspace is authorised to send dossier content to Anthropic's US endpoint.
 * The hint follows that flag instead of asserting one of the two.
 */
import type { TranslationKey } from '@/i18n'
import type { WorkspaceRef } from '@/api/types'

export const ALL_DATA_MODES = ['demo', 'anonymised', 'live'] as const
export type DataMode = (typeof ALL_DATA_MODES)[number]

export const DATA_MODE_LABEL: Record<DataMode, TranslationKey> = {
  demo: 'workspace.mode.demo',
  anonymised: 'workspace.mode.anonymised',
  live: 'workspace.mode.live',
}

export const DATA_MODE_HINT: Record<DataMode, TranslationKey> = {
  demo: 'workspace.mode.demoHint',
  anonymised: 'workspace.mode.anonymisedHint',
  live: 'workspace.mode.liveHint',
}

/**
 * Why a mode this server does not offer is not offered. Per mode, because the
 * only interesting answer is `live`'s and a generic sentence taught nobody what
 * the mode would even do.
 */
export const DATA_MODE_LOCKED_REASON: Record<DataMode, TranslationKey> = {
  demo: 'workspace.mode.lockedReason',
  anonymised: 'workspace.mode.lockedReason',
  live: 'workspace.mode.liveLockedReason',
}

/** The hint for a mode, given what this server actually does with the data. */
export function dataModeHint(workspace: WorkspaceRef, mode: DataMode): TranslationKey {
  if (mode === 'live' && workspace.us_transfer) return 'workspace.mode.liveHintUs'
  return DATA_MODE_HINT[mode]
}

/**
 * The attestation the uploader is asked to tick, keyed the same way the server
 * keys it (`app/datamodes.attestation_text`). Three sentences, because there is
 * no single one that is true in all three states — and asking someone to
 * confirm "contains no real personal data" on a live dossier would be worse
 * than asking nothing. The server sends the German original as
 * `WorkspaceRef.attestation_de`; this picks the localised twin of it.
 */
export function attestationKey(workspace: WorkspaceRef): TranslationKey {
  if (workspace.data_mode !== 'live') return 'upload.attestCheckbox'
  return workspace.us_transfer
    ? 'upload.attestCheckboxLiveUs'
    : 'upload.attestCheckboxLiveLocal'
}

export function attestationHelpKey(workspace: WorkspaceRef): TranslationKey {
  return workspace.data_mode === 'live' ? 'upload.attestHelpLive' : 'upload.attestHelp'
}

export function isSelectable(workspace: WorkspaceRef, mode: string): boolean {
  return workspace.data_mode_options.includes(mode)
}
